<template>
  <div v-loading="scopesLoading" class="custom-alerts">
    <CountryScopeRail
      :entries="railEntries"
      :selected-iso="selectedIso"
      @select="selectScope"
    >
      <template #footer>
        <el-select
          v-if="addableIsos.length"
          class="add-country"
          :model-value="''"
          filterable
          placeholder="Add country"
          size="small"
          @change="addCountry"
        >
          <el-option
            v-for="iso in addableIsos"
            :key="iso"
            :label="iso"
            :value="iso"
          />
        </el-select>
      </template>
    </CountryScopeRail>

    <section class="scope-editor">
      <DetectionsPanel
        :catalog="catalog"
        :loading="catalogLoading"
        :saving="saving"
        editable
        :hint="hint"
        @save="save"
      />
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import { fetchDetections, fetchScopes, updateDetections } from '@/api/ad_scans'
import { IDetectionCatalog, IDetectionScope } from '@/interfaces/ad_scans'
import { ElMessage } from 'element-plus'
import DetectionsPanel from './DetectionsPanel.vue'
import CountryScopeRail from './CountryScopeRail.vue'

export default defineComponent({
  name: 'CustomAlertsTab',
  components: { DetectionsPanel, CountryScopeRail },
  props: {
    sourceableType: {
      type: String,
      default: ''
    },
    sourceableId: {
      type: String,
      default: ''
    },
    active: {
      type: Boolean,
      default: false
    }
  },
  emits: ['updated'],
  setup(props, { emit }) {
    const scopesLoading = ref(false)
    const catalogLoading = ref(false)
    const saving = ref(false)
    const catalog = ref<IDetectionCatalog | null>(null)
    const scopes = ref<IDetectionScope[]>([])
    const availableIsos = ref<string[]>([])
    const selectedIso = ref<string | null>(null)

    const globalCount = computed(
      () => scopes.value.find((scope) => scope.iso === null)?.count ?? 0
    )
    const countryScopes = computed(() =>
      scopes.value.filter((scope) => scope.iso !== null)
    )
    const addableIsos = computed(() =>
      availableIsos.value.filter(
        (iso) => !scopes.value.some((scope) => scope.iso === iso)
      )
    )
    const railEntries = computed(() => [
      {
        iso: null,
        label: 'Global',
        sub: `all countries · ${globalCount.value} alerts`
      },
      ...countryScopes.value.map((scope) => ({
        iso: scope.iso,
        label: scope.iso as string,
        sub: `${scope.count} alerts`
      }))
    ])
    const hint = computed(() =>
      selectedIso.value === null
        ? 'Applies to all countries unless a specific country override exists.'
        : `Overrides Global for ${selectedIso.value} traffic. Saving with no alerts clears them for this country.`
    )

    const loadScopes = async () => {
      scopesLoading.value = true
      try {
        const result = await fetchScopes({
          sourceable_type: props.sourceableType,
          sourceable_id: props.sourceableId
        })
        scopes.value = result.scopes ?? []
        availableIsos.value = result.targeting_isos ?? []
        emit('updated', countryScopes.value.length > 0 || globalCount.value > 0)
      } catch (e) {
        console.error('fetchScopes error:', e)
      } finally {
        scopesLoading.value = false
      }
    }

    const loadCatalog = async () => {
      catalogLoading.value = true
      try {
        const result = await fetchDetections({
          sourceable_type: props.sourceableType,
          sourceable_id: props.sourceableId,
          iso: selectedIso.value
        })
        // Edit only the MANUAL override for this scope. Overrides merge with (add
        // to) scan results, so we must not pre-check scan-derived alerts here —
        // seeding from the resolved/display set would freeze them into the override.
        catalog.value = structuredClone(result.custom)
      } catch (e) {
        console.error('fetchDetections error:', e)
      } finally {
        catalogLoading.value = false
      }
    }

    const selectScope = (iso: string | null) => {
      if (selectedIso.value === iso && catalog.value) return
      selectedIso.value = iso
      catalog.value = null
      loadCatalog()
    }

    const addCountry = (iso: string) => {
      if (!iso) return
      if (!scopes.value.some((scope) => scope.iso === iso)) {
        scopes.value = [...scopes.value, { iso, count: 0 }]
      }
      selectScope(iso)
    }

    const save = async (detections: Record<string, string[]>) => {
      saving.value = true
      try {
        await updateDetections({
          sourceable_type: props.sourceableType,
          sourceable_id: props.sourceableId,
          iso: selectedIso.value,
          detections
        })
        await loadScopes()
        await loadCatalog()
        ElMessage.success('Custom alerts updated')
      } catch (e) {
        console.error('updateDetections error:', e)
        ElMessage.error('Failed to update custom alerts')
      } finally {
        saving.value = false
      }
    }

    const reload = async () => {
      selectedIso.value = null
      catalog.value = null
      await loadScopes()
      await loadCatalog()
    }

    watch(
      () => [props.active, props.sourceableType, props.sourceableId] as const,
      ([active]) => {
        if (!active) return
        reload()
      },
      { immediate: true }
    )

    return {
      scopesLoading,
      catalogLoading,
      saving,
      catalog,
      scopes,
      selectedIso,
      globalCount,
      railEntries,
      addableIsos,
      hint,
      selectScope,
      addCountry,
      save
    }
  }
})
</script>

<style scoped>
.custom-alerts {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 16px;
  min-height: 260px;
}

.add-country {
  margin-top: 8px;
  width: 100%;
}

.scope-editor {
  min-width: 0;
}
</style>
